import { FeatureFlagName } from '@/api/gen/featureFlagName';
import { useAuthContext } from '@/contexts/authContext';
import { useFeatureFlagContext } from '@/contexts/featureFlagContext';
import { useSchoolSettingBySchoolId } from '@/hooks/school/useSchoolSettingBySchoolId';

type GamificationDeterminerResult = { isReady: boolean; isGamification: boolean };

export const useGamificationDeterminer = (): GamificationDeterminerResult => {
  const { user } = useAuthContext();

  const { isFeatureEnabled, isLoading: isFeatureFlagLoading } = useFeatureFlagContext();
  const isGamificationEnabled = isFeatureEnabled(FeatureFlagName.Y25Q3_Gamification);
  const {
    data: schoolSetting,
    error: schoolSettingError,
    isLoading: isSchoolSettingLoading,
    isValidating: isSchoolSettingValidating,
  } = useSchoolSettingBySchoolId(user?.schoolId);

  /**
   * A failed school-setting lookup is a settled state, not a pending one. SWR keeps retrying with
   * backoff and flips `isValidating` back to `true` on every attempt, so without the error branch a
   * consumer gating its render on `isReady` bounces back to a loading screen indefinitely.
   * Reporting ready on error is safe because `isGamification` below already falls back to the
   * feature flag alone whenever `schoolSetting` is unavailable.
   */
  const isSchoolSettingSettled = !!schoolSettingError || (!isSchoolSettingLoading && !isSchoolSettingValidating);
  const isReady = !isFeatureFlagLoading && isSchoolSettingSettled;

  /**
   * if `schoolSetting` is null (not logged-in),  \
   *  Then use `true` too allow "FeatureFlag" to control the result
   */
  const isSchoolUsingGamification = schoolSetting?.shouldUseGamification ?? true;

  /**
   * Whether to use Gamification or not,
   * If `schoolSetting` is unavailable, Then only use feature flag.
   */
  const isGamification = isGamificationEnabled && isSchoolUsingGamification;

  return {
    isReady,
    isGamification: isGamification,
  };
};
